/**
 * Map of field names to their validation functions.
 * Currently supports validation for singleStagePipelineId and unifiedPipelineId fields.
 */
const FIELD_VALIDATORS: Record<string, (v: any) => string[]> = {
  singleStagePipelineId: (v) => validatePipelineId(v),
  unifiedPipelineId: (v) => validatePipelineId(v),
};

/**
 * Validates that a pipeline ID contains only numeric digits.
 * Empty, null, and undefined values are considered valid (optional field).
 * @param v - The value to validate
 * @returns Array of error messages (empty if valid)
 */
const validatePipelineId = (v: any) => {
  const errors: string[] = [];
  if (v && !/^\d+$/.test(v.trim())) {
    errors.push("Must contain only digits.");
  }
  return errors;
};

/**
 * Validates a single pipeline configuration field and updates the error state.
 * @param name - The field name to validate
 * @param value - The field value to validate
 * @param setErrors - Function to update the error state, accepts either an error object or updater function
 * @returns Array of validation errors for this field (empty if valid)
 */
export function validateConfigurePipelineField(
  name: string,
  value: any,
  setErrors: (
    errors:
      | Record<string, string[]>
      | ((prevErrors: Record<string, string[]>) => Record<string, string[]>),
  ) => void,
) {
  if (!FIELD_VALIDATORS[name]) {
    return [];
  }
  const errors = FIELD_VALIDATORS[name](value);
  setErrors((prevErrors: Record<string, string[]>) => ({
    ...prevErrors,
    [name]: errors,
  }));
  return errors;
}

/**
 * Validates the entire pipeline configuration form.
 * Validates each field individually and also enforces that at least one pipeline ID must be provided.
 * @param data - The form data object containing field values
 * @param setErrors - Function to set the complete error state
 * @returns True if the form is valid, false if there are any validation errors
 */
export function validateConfigurePipelineForm(
  data: { [key: string]: any },
  setErrors: (errors: Record<string, string[]>) => void,
) {
  const errors: Record<string, string[]> = {};
  let hasErrors = false;
  const validationFields = Object.keys(FIELD_VALIDATORS);

  // Validate each field individually
  for (const key of validationFields) {
    const value = data[key];
    const fieldErrors = FIELD_VALIDATORS[key](value);
    if (fieldErrors.length > 0) {
      errors[key] = fieldErrors;
      hasErrors = true;
    }
  }

  // Form-level validation: at least one pipeline ID must be provided
  if (data.singleStagePipelineId === "" && data.unifiedPipelineId === "") {
    errors["form"] = ["At least one Pipeline ID must be provided."];
    hasErrors = true;
  }

  setErrors(errors);
  return !hasErrors;
}
